// 005 slice / 007 (P-HOME-1, P-HOME-2): the parent Home. Live Colour Tiles (DESIGN.md): the selected
// child's name at poster size, square child chips, the school's notice tile (its live banners), then
// colour tiles for the categories and the next step (a delivery address). Choosing a child at another
// school switches the banners, and the header follows (004 FR-026). Booklists come with the rest of 005.
import { type Href, Link, useRouter } from "expo-router";
import { type ComponentType, type ReactNode, useEffect, useState } from "react";
import {
  Animated,
  Platform,
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";

import { addStudentHref } from "@/join/handoff";
import { pendingJoin } from "@/join/pendingJoin";
import { selectStudent, useSelectedStudent } from "@/profile/selection";
import { useParentData } from "@/profile/useParentData";
import {
  addressVM,
  canAddChild,
  standardClassName,
} from "@/profile/viewModels";
import { useChromeKind } from "@/shell/chrome/useChromeKind";
import type { ColorRole } from "@/theme/tokens";
import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";
import { Reveal, usePressSink } from "@/ui/motion";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { type HeroBanner, HeroCarousel } from "./HeroCarousel";
import { useHomeClient } from "./homeApi";
import { BookStackArt, PencilArt, ShirtArt } from "./TileArt";

/**
 * Per school, whether its last banner load found any (this session only). Sizes the hero's loading
 * placeholder to what will replace it, so the page doesn't jump.
 */
const hadBanners = new Map<string, boolean>();

export const HOME_COPY = {
  shoppingFor: "Shopping for",
  heroFallback: "Books & stationery for the new session",
  categories: "Shop by category",
  comingSoon: "Coming soon",
  addChild: "Add a child",
  addAddress: "Add a delivery address",
  addAddressHint: "Needed before you order",
  deliverTo: (title: string) => `Deliver to ${title}`,
  /** P-HOME-5: web footer. */
  footer: "Supplied and delivered by Akaiva Edu Pvt Ltd",
  chip: (name: string, classLevel: string, section: string) =>
    `${name} · ${standardClassName(classLevel)} ${section}`,
} as const;

type ArtComponent = ComponentType<{ ink: string; size?: number }>;

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    scroll: { flex: 1 },
    page: {
      width: "100%",
      maxWidth: 1080,
      alignSelf: "center",
      paddingHorizontal: t.space.lg,
      paddingTop: t.space.sm,
      paddingBottom: t.space.x3l,
      gap: t.space.sm,
    },
    who: { gap: t.space.xxs, marginBottom: t.space.sm },
    lower: { textTransform: "lowercase" },
    // The child's name at poster size, cropped at the edge rather than shrunk.
    giant: {
      textTransform: "lowercase",
      letterSpacing: t.letterSpacing.display,
      marginLeft: t.letterSpacing.display,
    },
    chips: { flexDirection: "row", flexWrap: "wrap", gap: t.space.sm },
    chip: {
      paddingHorizontal: t.space.md,
      borderRadius: t.radius.button,
      borderWidth: t.borderWidth.input,
      borderColor: t.color.border,
      justifyContent: "center",
    },
    chipOn: { borderColor: t.color.accent, backgroundColor: t.color.accent },
    chipTextOn: { color: t.color.onAccent },
    chipHover: { borderColor: t.color.text },
    section: {
      marginTop: t.space.md,
      color: t.color.primaryText,
      textTransform: "lowercase",
    },
    row: { flexDirection: "row", gap: t.space.sm },
    noticeCol: { flex: 1 },
    grid: { flex: 1, gap: t.space.sm },
    // A grid cell: carries the arrival and the press sink around the tile itself.
    cell: { flex: 1 },
    tile: {
      flex: 1,
      minHeight: 168,
      borderRadius: t.radius.card,
      padding: t.space.md,
      alignItems: "flex-start",
      justifyContent: "flex-end",
      overflow: "hidden",
    },
    tileWide: { minHeight: 200, padding: t.space.lg },
    tileArt: { position: "absolute", right: t.space.xs, top: t.space.xs },
    tileHover: { opacity: 0.9 },
    footer: {
      width: "100%",
      maxWidth: 1080,
      alignSelf: "center",
      paddingHorizontal: t.space.lg,
      paddingBottom: t.space.x3l,
    },
  }),
);

export type TileProps = {
  label: string;
  caption?: string;
  fill: ColorRole;
  ink: ColorRole;
  Art?: ArtComponent;
  /** Pressable tiles navigate; without it the tile is informational (e.g. "Coming soon"). */
  href?: Href;
  /** A different colour for the caption line (the next-step prompt). */
  captionColor?: string;
  /** Position in the wall's arrival sequence. */
  index?: number;
  testID: string;
};

export function Tile({
  label,
  caption,
  fill,
  ink,
  Art,
  href,
  captionColor,
  index = 0,
  testID,
}: TileProps) {
  const styles = useStyles();
  const theme = useTheme();
  const sink = usePressSink();
  const wide = useChromeKind() === "desktop";
  const color = theme.color[ink];
  const body: ReactNode = (
    <>
      {Art ? (
        <View style={styles.tileArt}>
          <Art ink={color} size={wide ? 112 : 88} />
        </View>
      ) : null}
      <Text
        weight="light"
        size={wide ? "x4l" : "x3l"}
        style={[styles.lower, { color }]}
        numberOfLines={2}
      >
        {label}
      </Text>
      {caption ? (
        <Text
          size="sm"
          weight="semiBold"
          style={[styles.lower, { color: captionColor ?? color }]}
          numberOfLines={2}
        >
          {caption}
        </Text>
      ) : null}
    </>
  );
  const style = StyleSheet.flatten([
    styles.tile,
    wide && styles.tileWide,
    { backgroundColor: theme.color[fill] },
  ]);
  if (!href) {
    return (
      <Reveal index={index} style={styles.cell}>
        <View
          style={style}
          testID={testID}
          accessible
          accessibilityLabel={[label, caption].filter(Boolean).join(", ")}
        >
          {body}
        </View>
      </Reveal>
    );
  }
  return (
    <Reveal index={index} style={styles.cell}>
      <Animated.View style={[styles.cell, sink.style]}>
        <Link href={href} asChild>
          <PressableTarget
            accessibilityRole="link"
            accessibilityLabel={label}
            // Link asChild hands the style to the DOM on the web: it must be one object, not an array.
            style={style}
            hoveredStyle={styles.tileHover}
            onPressIn={sink.onPressIn}
            onPressOut={sink.onPressOut}
            testID={testID}
          >
            {body}
          </PressableTarget>
        </Link>
      </Animated.View>
    </Reveal>
  );
}

/** "CLASS_1" + "A" → "1A"; other levels keep their name ("Nursery A"). */
function shortClass(classLevel: string, section: string): string {
  const name = standardClassName(classLevel);
  return name.startsWith("Class ")
    ? `${name.slice(6)}${section}`
    : `${name} ${section}`;
}

export function HomeScreen() {
  const styles = useStyles();
  const theme = useTheme();
  const router = useRouter();
  const wide = useChromeKind() === "desktop";
  const client = useHomeClient();
  const { students, addresses, actions } = useParentData();
  const selectedId = useSelectedStudent();
  const all = students.data ?? [];
  const linked = all.filter((s) => s.linked && s.school !== null);
  const selected = linked.find((s) => s.id === selectedId) ?? linked[0] ?? null;
  const schoolId = selected?.school?.id ?? null;
  const [banners, setBanners] = useState<{
    schoolId: string;
    items: HeroBanner[];
  } | null>(null);
  // Pull to refresh (phones): the children, addresses and this school's banners again.
  const [reloads, setReloads] = useState(0);
  const [pulling, setPulling] = useState(false);
  const pullToRefresh = () => {
    setPulling(true);
    setReloads((n) => n + 1);
    void actions.refresh().finally(() => setPulling(false));
  };

  useEffect(() => {
    if (!schoolId) return;
    let live = true;
    client.listLiveSchoolBanners(schoolId).then(
      (list) => {
        hadBanners.set(schoolId, list.items.length > 0);
        if (live) setBanners({ schoolId, items: list.items });
      },
      () => {
        // The hero falls back to the school's notice; Home stays usable (P-HOME-1).
        hadBanners.set(schoolId, false);
        if (live) setBanners({ schoolId, items: [] });
      },
    );
    return () => {
      live = false;
    };
  }, [client, schoolId, reloads]);

  const loaded = banners !== null && banners.schoolId === schoolId;
  const items = loaded ? banners.items : [];

  // The next step: where orders go, or the address still missing (BR-1: no order without a pin).
  const addressList = (addresses.data ?? []).map(addressVM);
  const home = addressList.find((a) => a.isDefault) ?? addressList[0] ?? null;
  const missingAddress =
    addresses.data !== null && home === null && selected !== null;

  const addChild = () =>
    void pendingJoin
      .read()
      .then((p) =>
        router.push(
          p ? addStudentHref(p.code, p.source) : "/enter-school-code?mode=add",
        ),
      );

  const [first, ...rest] = (selected?.name ?? "").split(" ");
  const nextTile = home ? (
    <Tile
      testID="home-next-step"
      index={5}
      label={HOME_COPY.deliverTo(home.title)}
      caption={home.oneLine}
      fill="tilePink"
      ink="onTile"
      href="/profile"
    />
  ) : (
    <Tile
      testID="home-next-step"
      index={5}
      label={HOME_COPY.addAddress}
      caption={HOME_COPY.addAddressHint}
      fill="tilePink"
      ink="onTile"
      href="/addresses/new"
    />
  );
  const books = (
    <Tile
      testID="home-category-books"
      index={2}
      label="School books"
      caption={
        selected ? standardClassName(selected.classLevel ?? "") : undefined
      }
      fill="tileAmber"
      ink="onTileAmber"
      Art={BookStackArt}
      href="/shop"
    />
  );
  const stationery = (
    <Tile
      testID="home-category-stationery"
      index={3}
      label="Stationery"
      caption="Notebooks, pens & more"
      fill="tileGreen"
      ink="onTile"
      Art={PencilArt}
      href="/shop"
    />
  );
  const uniforms = (
    <Tile
      testID="home-category-uniforms"
      index={4}
      label="Uniforms"
      caption={HOME_COPY.comingSoon}
      fill="tileMuted"
      ink="onTileMuted"
      Art={ShirtArt}
    />
  );

  const hero = (school: { id: string; name: string }, fill = wide) => (
    <Reveal index={1} style={fill ? styles.cell : undefined}>
      <HeroCarousel
        banners={items}
        schoolName={school.name}
        fallbackText={HOME_COPY.heroFallback}
        loading={!loaded}
        expectBanners={hadBanners.get(school.id) ?? true}
        fill={fill}
      />
    </Reveal>
  );
  const categoriesHeading = (
    <Text
      weight="light"
      size="x3l"
      style={styles.section}
      accessibilityRole="header"
      aria-level={selected ? 2 : 1}
    >
      {HOME_COPY.categories}
    </Text>
  );

  return (
    <ScrollView
      style={styles.scroll}
      testID="home-screen"
      refreshControl={
        wide ? undefined : (
          <RefreshControl
            refreshing={pulling}
            onRefresh={pullToRefresh}
            colors={[theme.color.tileBlue]}
            progressBackgroundColor={theme.color.surface}
          />
        )
      }
    >
      <View style={styles.page} role="main">
        {selected ? (
          <View style={styles.who}>
            {/* Re-keyed per child: switching child glides the new name in from the right. */}
            <Reveal key={selected.id} from="slide">
              <Text
                weight="bold"
                size={wide ? "x9l" : "x8l"}
                numberOfLines={1}
                ellipsizeMode="clip"
                style={[
                  styles.giant,
                  { lineHeight: theme.fontSize[wide ? "x9l" : "x8l"] },
                ]}
                accessibilityRole="header"
                aria-level={1}
                accessibilityLabel={`${HOME_COPY.shoppingFor} ${selected.name}`}
              >
                {first}
                {rest.length > 0 ? (
                  <Text
                    weight="light"
                    size={wide ? "x9l" : "x8l"}
                    style={styles.giant}
                  >{` ${rest.join(" ")}`}</Text>
                ) : null}
              </Text>
            </Reveal>
            <View
              style={styles.chips}
              role="radiogroup"
              aria-label={HOME_COPY.shoppingFor}
            >
              {linked.map((s) => {
                const on = s.id === selected.id;
                return (
                  <PressableTarget
                    key={s.id}
                    accessibilityRole="radio"
                    accessibilityLabel={HOME_COPY.chip(
                      s.name,
                      s.classLevel ?? "",
                      s.section ?? "",
                    )}
                    accessibilityState={{ checked: on }}
                    aria-checked={on}
                    onPress={() => selectStudent(s.id)}
                    style={on ? [styles.chip, styles.chipOn] : styles.chip}
                    hoveredStyle={on ? undefined : styles.chipHover}
                  >
                    <Text
                      weight="semiBold"
                      style={on ? styles.chipTextOn : undefined}
                    >
                      {`${s.name.split(" ")[0]} · ${shortClass(s.classLevel ?? "", s.section ?? "")}`}
                    </Text>
                  </PressableTarget>
                );
              })}
              {canAddChild(all.length) ? (
                <PressableTarget
                  accessibilityLabel={HOME_COPY.addChild}
                  onPress={addChild}
                  style={styles.chip}
                  hoveredStyle={styles.chipHover}
                >
                  <Text weight="semiBold" tone="muted">
                    + add
                  </Text>
                </PressableTarget>
              ) : null}
            </View>
          </View>
        ) : null}
        {wide && !selected ? categoriesHeading : null}
        {wide && loaded && items.length > 0 && selected?.school ? (
          // Desktop with live banners: a school's 1600 x 480 artwork keeps its full shape (cropping
          // it to the board would cut its text), with the colour tiles in one row beneath.
          <>
            {hero(selected.school, false)}
            <View style={styles.row}>
              {books}
              {stationery}
              {uniforms}
              {nextTile}
            </View>
          </>
        ) : wide ? (
          // Desktop: a mixed-size board, the notice tile beside a 2 x 2 of colour tiles.
          <View style={styles.row}>
            {selected?.school ? (
              <View style={styles.noticeCol}>{hero(selected.school)}</View>
            ) : null}
            <View style={styles.grid}>
              <View style={styles.row}>
                {books}
                {stationery}
              </View>
              <View style={styles.row}>
                {uniforms}
                {nextTile}
              </View>
            </View>
          </View>
        ) : (
          <>
            {/* No address yet: it is the one thing ordering needs, so it leads on a phone. */}
            {missingAddress ? <View style={styles.row}>{nextTile}</View> : null}
            {selected?.school ? hero(selected.school) : null}
            {categoriesHeading}
            <View style={styles.row}>
              {books}
              {stationery}
            </View>
            <View style={styles.row}>
              {uniforms}
              {missingAddress ? null : nextTile}
            </View>
          </>
        )}
      </View>
      {Platform.OS === "web" ? (
        <View style={styles.footer} role="contentinfo">
          <Text size="sm" tone="muted">
            {HOME_COPY.footer}
          </Text>
        </View>
      ) : null}
    </ScrollView>
  );
}
